// =====================================================================================
// useEditMode Hook - Convenient access to EditModeContext
// =====================================================================================
// Provides easy access to decoration edit mode state and operations
// Handles WebSocket events and tracks other users' editing sessions

import { useContext, useEffect, useMemo, useState } from 'react';
import { EditModeContext } from '../contexts/editModeContext.js';
import { useAppContext } from './useAppContext.js';

/**
 * Interface for tracking other users currently editing
 */
interface EditingUser {
  userId: string;
  username: string;
  mode: string;
  lastActivity: number;
}

/**
 * Hook for accessing decoration edit mode functionality
 * Must be used within an EditModeProvider
 *
 * @example
 * ```tsx
 * export function MyComponent() {
 *   const editMode = useEditMode();
 *
 *   const handleEnterEditMode = () => {
 *     editMode.enterEditMode();
 *   };
 *
 *   return (
 *     <button onClick={handleEnterEditMode}>
 *       Start Editing
 *     </button>
 *   );
 * }
 * ```
 */
export function useEditMode() {
  const context = useContext(EditModeContext);
  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  const { roomConnection } = useAppContext();

  // Track other users currently editing
  const [editingUsers, setEditingUsers] = useState<EditingUser[]>([]);

  // Handle WebSocket events for tracking other editors
  useEffect(() => {
    const socket = roomConnection.socket;
    if (!socket) return;

    const handleUserStartedEditing = (data: { userId: string; username: string; mode: string }) => {
      // User started editing
      setEditingUsers(prev => {
        const filtered = prev.filter(u => u.userId !== data.userId);
        return [...filtered, {
          ...data,
          lastActivity: Date.now()
        }];
      });
    };

    const handleUserStoppedEditing = (data: { userId: string }) => {
      // User stopped editing
      setEditingUsers(prev => prev.filter(u => u.userId !== data.userId));
    };

    const handleUserEditModeChanged = (data: { userId: string; mode: string }) => {
      // User changed edit mode
      setEditingUsers(prev => prev.map(u =>
        u.userId === data.userId
          ? { ...u, mode: data.mode, lastActivity: Date.now() }
          : u
      ));
    };

    // Register event listeners
    socket.on('decorations:user_started_editing', handleUserStartedEditing);
    socket.on('decorations:user_stopped_editing', handleUserStoppedEditing);
    socket.on('decorations:user_mode_changed', handleUserEditModeChanged);

    return () => {
      socket.off('decorations:user_started_editing', handleUserStartedEditing);
      socket.off('decorations:user_stopped_editing', handleUserStoppedEditing);
      socket.off('decorations:user_mode_changed', handleUserEditModeChanged);
    };
  }, [roomConnection.socket]);

  // Notify server when entering/exiting edit mode
  useEffect(() => {
    const socket = roomConnection.socket;
    if (!socket) return;

    if (context.isActive) {
      socket.emit('decorations:start_editing', { mode: context.mode });
    } else {
      socket.emit('decorations:stop_editing');
    }
  }, [context.isActive, roomConnection.socket]);

  // Notify server when mode changes
  useEffect(() => {
    const socket = roomConnection.socket;
    if (!socket || !context.isActive) return;

    socket.emit('decorations:mode_changed', { mode: context.mode });
  }, [context.mode, context.isActive, roomConnection.socket]);

  // Clean up inactive editing users (after 30 seconds of inactivity)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setEditingUsers(prev =>
        prev.filter(u => now - u.lastActivity < 30000)
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Extended context with additional utilities (memoized to avoid re-creating every render)
  return useMemo(() => ({
    ...context,

    // Additional state
    editingUsers,
    isEditingWithOthers: editingUsers.length > 0,

    // Utility functions
    getEditingUserCount: () => editingUsers.length,
    getEditingUsernames: () => editingUsers.map(u => u.username),

    // Quick access to common operations
    toggleEditMode: () => {
      if (context.isActive) {
        context.exitEditMode();
      } else {
        context.enterEditMode();
      }
    },

    // Check if can edit (no conflicts with other users)
    canEdit: () => {
      // For now, allow multiple editors
      // This could be enhanced to prevent conflicts
      return true;
    },

    // Get filtered decorations by current category
    getFilteredDecorations: () => context.availableDecorations,

    // Check if decoration is selected
    isDecorationSelected: (decorationId: string) => {
      return context.selectedDecoration?.id === decorationId;
    },

    // Check if in erase mode
    isEraseMode: () => context.mode === 'erase',

    // Check if in place mode
    isPlaceMode: () => context.mode === 'place',

    // Check if in select mode
    isSelectMode: () => context.mode === 'select'
  }), [context, editingUsers]);
}